/**
 * OrganizationSelectPopup
 *
 * 店舗より上の階層（organization_level の first..tenth フラグ ON、level=0..9）
 * を選択するためのポップアップ。
 *
 * StoreSwitchPopup と異なり、店舗 (level=11) や部門 (level=12) は選べない。
 * 店舗の登録・編集の「所属組織」欄など、新店舗の親組織を指定する用途で使う。
 */
import { useState, useEffect, useCallback, useRef, useMemo, type FC } from "react";
import { createPortal } from "react-dom";
import {
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import { fetchOrganizations, type OrganizationResponse } from "../../../api/organizations";
import {
    fetchMyOrganizationLevel,
    LEVEL_FLAG_LABEL,
    LEVEL_FLAG_ORDER,
    LEVEL_FLAG_TO_ORG_LEVEL,
    type OrganizationLevelFlagKey,
    type OrganizationLevelResponse,
} from "../../../api/organizationLevels";

// =============================================================================
// 型定義
// =============================================================================

interface ActiveOrgRow {
    flagKey: OrganizationLevelFlagKey;
    orgLevel: number;
    label: string;
}

/**
 * 選択結果。orgLevels は organization.level をキー (0..9) とする。
 * lockFirstLevel=true の時は first レベルが自動 lock される。
 */
export interface OrganizationSelection {
    orgLevels: Record<number, { id: number; name: string } | null>;
}

export interface OrganizationSelectPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (selection: OrganizationSelection) => void;
    currentSelection?: OrganizationSelection;
    anchorRef: React.RefObject<HTMLElement | null>;
    /** 第1階層 (root レベル) の選択を非活性化 */
    lockFirstLevel?: boolean;
}

// --- モジュールレベルキャッシュ ---
const CACHE_TTL = 5 * 60 * 1000;
interface PopupDataCache {
    organizationLevel: OrganizationLevelResponse;
    organizations: OrganizationResponse[];
    activeRows: ActiveOrgRow[];
    timestamp: number;
}
let dataCache: PopupDataCache | null = null;

/** organization_level のフラグから上位階層 (store/department/section 以外) を抽出 */
function buildActiveOrgRows(level: OrganizationLevelResponse): ActiveOrgRow[] {
    const rows: ActiveOrgRow[] = [];
    for (const flagKey of LEVEL_FLAG_ORDER) {
        if (!level[flagKey]) continue;
        // 上位階層 (first..tenth) のみ。store / department / section は除外
        if (flagKey === "store" || flagKey === "department" || flagKey === "section") continue;
        const orgLevel = LEVEL_FLAG_TO_ORG_LEVEL[flagKey];
        rows.push({ flagKey, orgLevel, label: LEVEL_FLAG_LABEL[flagKey] });
    }
    return rows;
}

// =============================================================================
// 汎用 Combobox 行
// =============================================================================

interface ComboboxRowProps {
    label: string;
    value: OrganizationResponse | null;
    onChange: (value: OrganizationResponse | null) => void;
    options: OrganizationResponse[];
    query: string;
    onQueryChange: (q: string) => void;
    loading?: boolean;
    disabled?: boolean;
}

const OrgComboboxRow: FC<ComboboxRowProps> = ({
    label,
    value,
    onChange,
    options,
    query,
    onQueryChange,
    loading = false,
    disabled = false,
}) => (
    <div className={clsx("flex items-center gap-3", disabled && "opacity-50")}>
        <label className="text-xs text-gray-500 w-24 shrink-0 text-right">{label}</label>
        <Combobox
            value={value}
            onChange={onChange}
            onClose={() => onQueryChange("")}
            disabled={disabled}
            immediate
        >
            <div className="relative flex-1">
                <ComboboxInput
                    className={clsx(
                        "w-full rounded-md border px-3 py-2 pr-8 text-sm transition-colors duration-200",
                        disabled
                            ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-white border-gray-300 hover:border-gray-400 text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 cursor-pointer",
                    )}
                    displayValue={(item: OrganizationResponse | null) => item?.name ?? ""}
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder="検索..."
                    readOnly
                    inputMode="none"
                />
                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg
                        className={clsx("w-4 h-4", disabled ? "text-gray-300" : "text-gray-400")}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </ComboboxButton>
            </div>
            <ComboboxOptions
                anchor="bottom start"
                className="z-[10000] w-[var(--input-width)] max-h-40 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
            >
                {options.length === 0 ? (
                    <div className="px-3 py-2 text-gray-400 text-xs">
                        {loading ? "読み込み中..." : "候補がありません"}
                    </div>
                ) : (
                    options.map((item) => (
                        <ComboboxOption
                            key={item.id}
                            value={item}
                            className="cursor-pointer select-none px-3 py-1.5 data-[focus]:bg-brand-50 data-[focus]:text-brand-700"
                        >
                            {item.name}
                        </ComboboxOption>
                    ))
                )}
            </ComboboxOptions>
        </Combobox>
    </div>
);

// =============================================================================
// メインコンポーネント
// =============================================================================

export const OrganizationSelectPopup: FC<OrganizationSelectPopupProps> = ({
    isOpen,
    onClose,
    onSelect,
    currentSelection,
    anchorRef,
    lockFirstLevel = false,
}) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);

    // 全データ
    const [allOrgs, setAllOrgs] = useState<OrganizationResponse[]>([]);
    const [activeRows, setActiveRows] = useState<ActiveOrgRow[]>([]);
    const [loading, setLoading] = useState(false);

    // 選択値・検索クエリ
    const [selectedOrgLevels, setSelectedOrgLevels] = useState<Record<number, OrganizationResponse | null>>({});
    const [orgQueries, setOrgQueries] = useState<Record<number, string>>({});

    // --- 位置計算 ---
    useEffect(() => {
        if (!isOpen) {
            setPosition(null);
            return;
        }
        if (!anchorRef.current) return;

        const updatePosition = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;
            const popupEl = popupRef.current;
            const pw = popupEl?.offsetWidth ?? 420;
            const ph = popupEl?.offsetHeight ?? 200;
            const gap = 4;
            const pad = 8;

            let top: number;
            let left: number;
            const fitsBelow = rect.bottom + gap + ph <= window.innerHeight - pad;
            const fitsAbove = rect.top - gap - ph >= pad;

            if (fitsBelow) {
                top = rect.bottom + gap;
                left = rect.left;
            } else if (fitsAbove) {
                top = rect.top - ph - gap;
                left = rect.left;
            } else {
                top = Math.max(pad, Math.min(rect.top, window.innerHeight - ph - pad));
                left = rect.right + gap;
            }
            if (left + pw > window.innerWidth - pad) {
                left = Math.max(pad, window.innerWidth - pw - pad);
            }
            setPosition({ top, left, width: rect.width });
        };

        const rafId = requestAnimationFrame(updatePosition);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen, anchorRef, loading]);

    // --- 全データ取得 ---
    useEffect(() => {
        if (!isOpen) return;
        // open 時に query は毎回リセット
        setOrgQueries({});

        if (dataCache && Date.now() - dataCache.timestamp < CACHE_TTL) {
            setAllOrgs(dataCache.organizations);
            setActiveRows(dataCache.activeRows);
            return;
        }
        setLoading(true);
        Promise.all([fetchMyOrganizationLevel(), fetchOrganizations()])
            .then(([levelRes, orgsRes]) => {
                const rows = buildActiveOrgRows(levelRes);
                dataCache = {
                    organizationLevel: levelRes,
                    organizations: orgsRes.items,
                    activeRows: rows,
                    timestamp: Date.now(),
                };
                setAllOrgs(orgsRes.items);
                setActiveRows(rows);
            })
            .catch((err) => console.error("OrganizationSelectPopup データ取得失敗:", err))
            .finally(() => setLoading(false));
    }, [isOpen]);

    // --- 初期値の反映 ---
    useEffect(() => {
        if (!currentSelection || allOrgs.length === 0 || activeRows.length === 0) return;
        const restored: Record<number, OrganizationResponse | null> = {};
        for (const row of activeRows) {
            const sel = currentSelection.orgLevels[row.orgLevel];
            if (sel) {
                const found = allOrgs.find((o) => o.id === sel.id);
                if (found) restored[row.orgLevel] = found;
            }
        }
        setSelectedOrgLevels(restored);
    }, [currentSelection, allOrgs, activeRows]);

    // --- 第1階層 (level=0) の自動選択（テナントルートが 1 件のときのみ） ---
    useEffect(() => {
        if (!isOpen || allOrgs.length === 0 || activeRows.length === 0) return;
        const firstRow = activeRows.find((r) => r.orgLevel === 0);
        if (!firstRow) return;
        if (selectedOrgLevels[0] != null) return;
        const rootCandidates = allOrgs.filter((o) => o.level === 0);
        if (rootCandidates.length === 1) {
            setSelectedOrgLevels((prev) => ({ ...prev, 0: rootCandidates[0] }));
        }
    }, [isOpen, allOrgs, activeRows, selectedOrgLevels]);

    // --- 階層ごとのフィルタ済み選択肢（親で絞り込み） ---
    const filteredOptions = useMemo(() => {
        const result: Record<number, OrganizationResponse[]> = {};
        for (let i = 0; i < activeRows.length; i++) {
            const row = activeRows[i];
            let items = allOrgs.filter((o) => o.level === row.orgLevel);
            // 親レベルで絞り込み
            for (let j = i - 1; j >= 0; j--) {
                const parentRow = activeRows[j];
                const parentSel = selectedOrgLevels[parentRow.orgLevel];
                if (parentSel) {
                    items = items.filter((o) => o.parent_organization_id === parentSel.id);
                    break;
                }
            }
            const q = orgQueries[row.orgLevel] ?? "";
            if (q) items = items.filter((o) => (o.name ?? "").includes(q));
            result[row.orgLevel] = items;
        }
        return result;
    }, [allOrgs, activeRows, selectedOrgLevels, orgQueries]);

    // --- 階層選択ハンドラ ---
    const handleOrgLevelChange = useCallback(
        (orgLevel: number, value: OrganizationResponse | null) => {
            setSelectedOrgLevels((prev) => {
                const next: Record<number, OrganizationResponse | null> = { ...prev, [orgLevel]: value };
                // 下位階層をクリア
                for (const row of activeRows) {
                    if (row.orgLevel > orgLevel) next[row.orgLevel] = null;
                }
                return next;
            });
        },
        [activeRows],
    );

    // --- 決定 ---
    const isComplete = useMemo(() => {
        if (activeRows.length === 0) return false;
        return activeRows.every((row) => selectedOrgLevels[row.orgLevel] != null);
    }, [activeRows, selectedOrgLevels]);

    const handleConfirm = useCallback(() => {
        if (!isComplete) return;
        const orgLevels: Record<number, { id: number; name: string } | null> = {};
        for (const row of activeRows) {
            const sel = selectedOrgLevels[row.orgLevel];
            orgLevels[row.orgLevel] = sel ? { id: sel.id, name: sel.name ?? "" } : null;
        }
        onSelect({ orgLevels });
        onClose();
    }, [isComplete, activeRows, selectedOrgLevels, onSelect, onClose]);

    // --- 外側クリックで閉じる ---
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest?.("[role='listbox']")) return;
            if (
                popupRef.current &&
                !popupRef.current.contains(target) &&
                anchorRef.current &&
                !anchorRef.current.contains(target)
            ) {
                onClose();
            }
        };
        const timer = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose, anchorRef]);

    if (!isOpen || loading) return null;
    const positioned = position !== null;

    return createPortal(
        <div
            ref={popupRef}
            className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[320px]"
            style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                width: position ? Math.max(position.width, 320) : undefined,
                visibility: positioned ? "visible" : "hidden",
                pointerEvents: positioned ? "auto" : "none",
            }}
        >
            <div className="space-y-3">
                {activeRows.map((row, index) => {
                    const isFirst = index === 0;
                    const disabled = lockFirstLevel && isFirst;
                    return (
                        <OrgComboboxRow
                            key={`row-${row.flagKey}`}
                            label={row.label}
                            value={selectedOrgLevels[row.orgLevel] ?? null}
                            onChange={(v) => handleOrgLevelChange(row.orgLevel, v)}
                            options={filteredOptions[row.orgLevel] ?? []}
                            query={orgQueries[row.orgLevel] ?? ""}
                            onQueryChange={(q) => setOrgQueries((prev) => ({ ...prev, [row.orgLevel]: q }))}
                            loading={loading}
                            disabled={disabled}
                        />
                    );
                })}
            </div>

            <div className="mt-4 flex justify-end">
                <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!isComplete}
                    className={clsx(
                        "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                        isComplete
                            ? "bg-brand-500 text-white hover:bg-brand-600"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed",
                    )}
                >
                    決定
                </button>
            </div>
        </div>,
        document.body,
    );
};

export default OrganizationSelectPopup;
